<template>
    <EntityGrid
        title="Domains"
        :entities="domains"
        :is-editing="isEditing"
        addButtonLabel="Add Domain"
        @add="openNewDomainDialog()"
    >
        <EntityCard
            v-for="domain in domains"
            :key="domain.identity"
            :entity="domain"
            :entityType="EntityType.DOMAIN"
            border-color="var(--stekz-primary-color)"
            :to="getDomainRoute(domain.identity)"
            :tags="[
                {
                    label: productsText(domain),
                    severity: 'primary',
                },
                {
                    label: processesText(domain),
                    severity: 'secondary',
                },
            ]"
        />
        <template #empty>
            <div class="empty-state">
                <i class="pi pi-folder-open empty-icon"></i>
                <div>
                    <h3>No domains yet</h3>
                    <i>
                        <p v-if="isEditing">
                            Create your first domain to get started.
                        </p>
                        <p v-else>
                            To add domains, go into edit mode from the top of
                            the page.
                        </p>
                    </i>
                </div>
                <Button
                    v-if="isEditing"
                    label="Add Domain"
                    icon="pi pi-plus"
                    severity="primary"
                    @click="openNewDomainDialog()"
                />
            </div>
        </template>
    </EntityGrid>
</template>

<script setup lang="ts">
import { useDialog } from "primevue/usedialog";
import { computed } from "vue";
import { useRoute } from "vue-router";

import EntityCard from "@/components/EntityInfo/EntityCard.vue";
import EntityGrid from "@/components/EntityInfo/EntityGrid.vue";
import type { DomainInTree } from "@/stores/domains-store";
import { EntityType } from "@/stores/entity-store";

import NewDomain from "./NewDomain.vue";

defineProps<{
    domains: DomainInTree[];
    isEditing?: boolean;
}>();

const route = useRoute();
const dialog = useDialog();

const hasValidRouteParams = computed(() =>
    Boolean(route.params.orgName && route.params.stekName)
);

const getDomainRoute = (domainId: string) => {
    if (!hasValidRouteParams.value) return undefined;
    return {
        name: "domainPage",
        params: {
            orgName: route.params.orgName,
            stekName: route.params.stekName,
            domainId,
        },
    };
};

const processCount = computed(() => {
    return (domain: DomainInTree) => {
        return domain.products.reduce(
            (total, product) => total + product.processes.length,
            0
        );
    };
});

const processesText = computed(() => {
    return (domain: DomainInTree) => {
        const count = processCount.value(domain);
        if (count === 0) {
            return "No processes";
        } else if (count === 1) {
            return `${count} process`;
        } else {
            return `${count} processes`;
        }
    };
});

const productsText = computed(() => {
    return (domain: DomainInTree) => {
        const count = domain.products.length;
        if (count === 0) {
            return "No products";
        } else if (count === 1) {
            return `${count} product`;
        } else {
            return `${count} products`;
        }
    };
});

// Navigation now handled directly by RouterLink

const openNewDomainDialog = () => {
    dialog.open(NewDomain, {
        props: {
            header: "New domain",
            style: {
                maxWidth: "600px",
                width: "80%",
            },
            modal: true,
            maximizable: false,
            draggable: false,
        },
    });
};
</script>

<style lang="scss" scoped>
.empty-state {
    display: flex;
    flex-direction: column;
    gap: 1em;
    align-items: center;
    text-align: center;

    > * {
        margin: 0;
    }

    .empty-icon {
        font-size: 3rem;
    }
}
</style>
